import styled from "styled-components"

export const BooksPage = styled.div`
  padding-top: 7rem;
  a {
    color: inherit;
  }
  a:focus-visible {
    outline: 2px solid ${p => p.theme.colors.main};
    outline-offset: 6px;
  }
  @media (max-width: 600px) {
    padding-top: 4rem;
  }
`

export const Intro = styled.div`
  .eyebrow {
    color: ${p => p.theme.colors.main};
    text-transform: uppercase;
    letter-spacing: 0.18em;
    font-size: 1.2rem;
    font-weight: 800;
  }
  h1 {
    font-size: clamp(4.8rem, 8vw, 7.6rem);
    line-height: 1.15;
    margin: 1.5rem 0 2rem;
  }
  > p {
    font-family: ${p => p.theme.fontFamily.text};
    font-size: 2.2rem;
    line-height: 1.65;
    max-width: 49rem;
    color: ${p => p.theme.colors.font.secondary};
  }
  .source {
    display: flex;
    flex-wrap: wrap;
    gap: 1.2rem 2.4rem;
    margin: 2.5rem 0 1.5rem;
  }
  a {
    font-size: 1.4rem;
    text-decoration: none;
    border-bottom: 1px solid ${p => p.theme.colors.border};
    padding-bottom: 0.4rem;
  }
  a:hover {
    color: ${p => p.theme.colors.main};
  }
  > small {
    color: ${p => p.theme.colors.font.secondary};
    font-size: 1.2rem;
  }
`

export const ShelfNav = styled.nav`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem 2.6rem;
  padding: 2rem 0;
  margin: 4rem 0 1rem;
  border-top: 1px solid ${p => p.theme.colors.borderLight};
  border-bottom: 1px solid ${p => p.theme.colors.borderLight};
  a {
    display: flex;
    align-items: center;
    gap: 0.8rem;
    text-decoration: none;
    font-size: 1.4rem;
    font-weight: 700;
  }
  a:hover {
    color: ${p => p.theme.colors.main};
  }
  span {
    background: ${p => p.theme.colors.secondary};
    color: ${p => p.theme.colors.main};
    padding: 0.2rem 0.7rem;
    border-radius: 4px;
    font-size: 1.2rem;
  }
`

export const Shelf = styled.section`
  details {
    margin-top: 3rem;
  }
  summary {
    cursor: pointer;
    color: ${p => p.theme.colors.main};
    font-size: 1.4rem;
    font-weight: 700;
    padding: 1.6rem 0;
    border-top: 1px solid ${p => p.theme.colors.borderLight};
  }
  summary:focus-visible {
    outline: 2px solid ${p => p.theme.colors.main};
    outline-offset: 4px;
  }
  details[open] summary {
    margin-bottom: 2rem;
  }
  header .read-note {
    font-family: ${p => p.theme.fontFamily.main};
    font-size: 1.2rem;
    line-height: 1.6;
  }
  scroll-margin-top: 8rem;
  padding: 4rem 0;
  & + & {
    border-top: 1px solid ${p => p.theme.colors.borderLight};
  }
  header {
    display: flex;
    align-items: baseline;
    gap: 1.4rem;
    margin-bottom: 3rem;
  }
  .number {
    color: ${p => p.theme.colors.main};
    font-size: 1.2rem;
    font-weight: 800;
  }
  h2 {
    font-size: 2.8rem;
    line-height: 1.3;
    letter-spacing: -0.03em;
  }
  header p {
    font-family: ${p => p.theme.fontFamily.text};
    color: ${p => p.theme.colors.font.secondary};
    margin: 0.8rem 0 0;
  }
  .count {
    margin-left: auto;
    white-space: nowrap;
    font-size: 1.2rem;
    color: ${p => p.theme.colors.font.secondary};
  }
  @media (max-width: 400px) {
    h2 {
      font-size: 2.4rem;
    }
    header {
      gap: 0.8rem;
    }
  }
`

export const BookGrid = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 3.8rem 2.4rem;
  @media (max-width: 700px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
  @media (max-width: 480px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 3rem 2rem;
  }
`

export const Book = styled.li`
  min-width: 0;
  a {
    text-decoration: none;
    display: block;
  }
  h3 {
    font-size: 1.6rem;
    line-height: 1.4;
    margin: 1.5rem 0 0.5rem;
  }
  p {
    font-size: 1.3rem;
    line-height: 1.5;
    margin: 0;
    color: ${p => p.theme.colors.font.secondary};
  }
  > small {
    display: block;
    margin-top: 0.8rem;
    font-size: 1.2rem;
    color: ${p => p.theme.colors.font.secondary};
  }
  > small span {
    color: ${p => p.theme.colors.main};
  }
  a:hover h3 {
    color: ${p => p.theme.colors.main};
  }
  a:hover img,
  a:hover .cover-type {
    transform: translateY(-4px) rotate(-1deg);
  }
  @media (prefers-reduced-motion: reduce) {
    a:hover img,
    a:hover .cover-type {
      transform: none;
    }
  }
`

export const Cover = styled.div`
  aspect-ratio: 2 / 3;
  display: flex;
  align-items: flex-end;
  img,
  .cover-type {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 2px 5px 5px 2px;
    box-shadow: 2px 4px 6px #35323e15, 6px 10px 18px #35323e12;
    transition: transform 0.2s ease;
  }
  .cover-type {
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding: 2rem 1.5rem;
    border-left: 6px solid #3875fc33;
    background: ${p => p.theme.colors.secondary};
    color: #29446b;
    font-family: ${p => p.theme.fontFamily.text};
    font-size: 1.8rem;
    line-height: 1.35;
    overflow: hidden;
  }
  .cover-type small {
    font-family: ${p => p.theme.fontFamily.main};
    font-size: 1.1rem;
    margin-top: 1rem;
  }
  @media (prefers-reduced-motion: reduce) {
    img,
    .cover-type {
      transition: none;
    }
  }
`

export const Empty = styled.p`
  background: #f7f9fc;
  border-left: 2px solid ${p => p.theme.colors.secondary};
  padding: 2.4rem;
  color: ${p => p.theme.colors.font.secondary};
  font-family: ${p => p.theme.fontFamily.text};
  line-height: 1.6;
`
